import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'

// Zitadel human OIDC, authorization code + PKCE (evaluation §8 Phase 4).
//
// oidc-client-ts rather than a hand-rolled flow: PKCE itself is short, but
// state/nonce validation, token expiry and silent renew are where hand-rolled
// OIDC goes wrong, and this page reads company financials.
//
// A public client has no secret — that is what PKCE is for — so the client id
// baked into this bundle is not a credential.

export type AuthConfig = {
  issuer: string
  clientId: string
  orgId: string
  apiUrl: string
}

export function readConfig(): AuthConfig {
  return {
    issuer: (import.meta.env['VITE_ZITADEL_ISSUER'] as string | undefined) ?? '',
    clientId: (import.meta.env['VITE_ZITADEL_CLIENT_ID'] as string | undefined) ?? '',
    orgId: (import.meta.env['VITE_ZITADEL_ORG_ID'] as string | undefined) ?? '',
    apiUrl: (import.meta.env['VITE_API_URL'] as string | undefined) ?? '',
  }
}

export function isConfigured(config: AuthConfig): boolean {
  return (
    config.issuer !== '' && config.clientId !== '' && config.orgId !== '' && config.apiUrl !== ''
  )
}

/**
 * Scopes for the authorization request.
 *
 * `urn:zitadel:iam:org:id:<orgId>` is the load-bearing one and every other
 * internal app sets it (Vaultwarden, weblate, zk-cli — see the zitadel repo).
 * It pins the login to zookeep-internal. Without it Zitadel resolves the
 * Google identity across the whole instance, so a staff member whose Google
 * account is already linked to a user in another org signs in as that user —
 * which does not hold the internal project — and gets "This account does not
 * have access to this app" from has_project_check, no matter what grants the
 * zookeep-internal user was given.
 *
 * No project scope is needed for roles: the app sets
 * access_token_role_assertion, so Zitadel puts FINANCE_VIEWER / FINANCE_QUERY
 * into the access token, and the project id is already in `aud` because the
 * app belongs to that project.
 */
export function oidcScope(orgId: string): string {
  const base = 'openid profile email'
  return orgId === '' ? base : `${base} urn:zitadel:iam:org:id:${orgId}`
}

export function createUserManager(config: AuthConfig): UserManager {
  return new UserManager({
    authority: config.issuer,
    client_id: config.clientId,
    redirect_uri: `${window.location.origin}/`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: 'code',
    scope: oidcScope(config.orgId),
    // sessionStorage, not localStorage: a token for the finance database
    // should not outlive the tab it was issued to.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
    // The access token is what the API verifies; ask for it to be renewed
    // rather than letting a dashboard sit on an expired one and show errors.
    accessTokenExpiringNotificationTimeInSeconds: 60,
  })
}

/**
 * Complete a redirect callback if this load is one, otherwise return the
 * stored user. Returns null when nobody is signed in.
 */
export async function resolveUser(manager: UserManager): Promise<User | null> {
  const params = new URLSearchParams(window.location.search)
  if (params.has('code') && params.has('state')) {
    const user = await manager.signinRedirectCallback()
    // Strip the code/state from the address bar so a refresh (or a shared
    // link) does not replay a spent authorization code.
    window.history.replaceState({}, '', window.location.pathname)
    return user
  }
  if (params.has('error')) {
    const description = params.get('error_description') ?? params.get('error') ?? 'unknown'
    window.history.replaceState({}, '', window.location.pathname)
    throw new Error(`sign-in failed: ${description}`)
  }
  return manager.getUser()
}
